<template>
  <div id="sac-autocomplete">
    <input
      id="auto-complete"
      v-model="query"
      autocomplete="one-time-code"
      type="text"
      name="search"
      :placeholder="placeholder"
      aria-label="Search"
      @input="onSearchInput($event.target.value)"
    />
    <span v-if="error" class="sac-ac-error">
      {{ error }}
    </span>
    <div v-if="showList" class="autocomplete-items">
      <div v-if="loading" class="sac-loader">
        <img src="@shared/assets/img/general/loading.svg" alt="Loading" />
      </div>
      <fragment v-else-if="searchResult.length">
        <div
          v-for="(item, idx) in searchResult"
          :key="idx"
          type="button"
          @click="selectItem(item)"
          @keypress="selectItem(item)"
        >
          <div class="autocomplete-item">
            <div v-if="label">
              {{ item[label] }}
              <span v-if="optionalLabel">
                {{ item[optionalLabel] }}
              </span>
              <span v-if="extraLabel" class="sac__font-12 med">
                - {{ item[extraLabel] }}
              </span>
            </div>
            <div v-else>
              {{ item }}
            </div>
          </div>
        </div>
      </fragment>
      <div
        v-else-if="query.length > 0 && searchResult.length === 0"
        class="no-result"
      >
        <div class="row-space">
          <div>No result found..</div>
          <button
            v-if="addAction"
            class="action-button"
            type="button"
            @click="anotherAction"
          >
            {{ addText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'SACAutoComplete',
  props: {
    label: String,
    showAddress: Boolean,
    placeholder: String,
    optionalLabel: String,
    extraLabel: String,
    searchMethod: Function,
    select: Function,
    addText: String,
    addAction: Function,
    queryKey: String,
    error: String,
    defaultQuery: String,
    defaultItemSelector: Function,
  },
  data() {
    return {
      showList: false,
      query: this.defaultQuery ?? '',
      loading: false,
      searchResult: [],
      searchCounter: 0,
    };
  },
  created() {
    this.debouncedSearch = _.debounce(() => {
      this.changeSearch();
    }, 300);
  },
  async mounted() {
    if (this.defaultQuery) {
      await this.changeSearch();
      const item = this.searchResult.find((x) => this.defaultItemSelector(x, this.defaultQuery));
      if (item) {
        await this.selectItem(item);
      } else {
        this.showList = false;
      }
    }
  },
  methods: {
    selectItem(item) {
      this.select(item);
      if (this.label && this.optionalLabel) {
        this.query = `${item[this.label]} ${item[this.optionalLabel]}`;
      } else {
        this.query = item[this.label];
      }
      this.showList = false;
    },
    onSearchInput() {
      this.searchCounter++;
      this.debouncedSearch();
    },
    async changeSearch() {
      const requestId = ++this.searchCounter;
      this.loading = true;
      this.showList = this.query.length > 0;
      try {
        if (this.query.length) {
          const result = await this.searchMethod(this.query);
          if (requestId !== this.searchCounter) return;
          this.searchResult = result;
        } else {
          this.searchResult = [];
        }
      } finally {
        if (requestId === this.searchCounter) {
          this.loading = false;
        }
      }
    },
    anotherAction() {
      this.query = '';
      this.addAction();
    },
  },
};
</script>

<style lang="scss">
#sac-autocomplete {
  position: relative;
  display: inline-block;
  border-radius: 2px;
  width: 100%;

  input {
    border: 1px solid transparent;
    background-color: $NBLUE5;
    height: 40px;
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
    font-size: 16px;
  }

  .sac-ac-error {
    font-size: 0.875rem;
    color: $TOMATO100;
    font-weight: 500;
  }

  .autocomplete-items {
    position: absolute;
    z-index: 99;
    top: 48px;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow: auto;
    text-overflow: ellipsis;
    box-shadow: 0 1px 27px 12px rgba(9, 15, 32, 0.1);
  }

  .autocomplete-item {
    padding: 10px;
    cursor: pointer;
    background-color: $WHITE;
    border-bottom: 0.5px solid $N20;

    &:hover {
      background-color: $NBLUE100;
      color: $WHITE;
    }
  }

  .sac-loader {
    display: grid;
    justify-content: center;
    padding: 10px;
    background-color: $WHITE;
  }

  .no-result {
    padding: 10px;
    color: $N70;
    font-weight: 500;
    text-align: center;
    background-color: $WHITE;

    .action-button {
      text-decoration: underline;
      color: $NBLUE110;
      cursor: pointer;

      &:hover {
        color: $NBLUE70;
      }
    }
  }
}
</style>
